import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { driversApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Users,
  Phone,
  Mail,
  Calendar,
  IndianRupee,
  TrendingUp,
  Star,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Upload,
  Image as ImageIcon,
  FileText,
  Trash2,
  Download,
  Eye,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

const DOC_TYPES = ['Driving License', 'Aadhar Card', 'PAN Card', 'Passport', 'Police Verification', 'Medical Certificate', 'Other'];

const DriverDetail = () => {
  const { id } = useParams();
  const [driver, setDriver] = useState(null);
  const [profitability, setProfitability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState('Driving License');
  const imageInputRef = useRef(null);
  const docInputRef = useRef(null);

  const fetchData = async () => {
    try {
      const [driverRes, profitRes] = await Promise.all([
        driversApi.getById(id),
        driversApi.getDriverProfitability(id),
      ]);
      setDriver(driverRes.data);
      setProfitability(profitRes.data);
    } catch (error) {
      toast.error('Failed to fetch driver details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await driversApi.uploadImage(id, formData);
      toast.success('Image uploaded successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await driversApi.uploadDocument(id, docType, formData);
      toast.success('Document uploaded successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
      if (docInputRef.current) docInputRef.current.value = '';
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await driversApi.deleteImage(id, imageId);
      toast.success('Image deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await driversApi.deleteDocument(id, docId);
      toast.success('Document deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  if (loading) {
    return (
      <Layout title="Driver Details">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  if (!driver) {
    return (
      <Layout title="Driver Not Found">
        <div className="text-center py-12">
          <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">Driver not found</p>
          <Link to="/drivers">
            <Button className="mt-4">Back to Drivers</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const financials = profitability?.financials || {};

  const getLicenseStatus = () => {
    if (!driver.license_expiry) return { status: 'unknown', color: 'text-slate-500' };
    const expiry = new Date(driver.license_expiry);
    const today = new Date();
    const daysLeft = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    
    if (daysLeft < 0) return { status: 'Expired', color: 'text-red-500' };
    if (daysLeft <= 30) return { status: `${daysLeft} days left`, color: 'text-yellow-500' };
    return { status: 'Valid', color: 'text-emerald-500' };
  };

  const licenseStatus = getLicenseStatus();

  return (
    <Layout
      title={driver.name}
      subtitle="Driver Profile"
      actions={
        <Link to="/drivers">
          <Button variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Drivers
          </Button>
        </Link>
      }
    >
      <div className="space-y-6" data-testid="driver-detail-page">
        {/* Driver Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="md:col-span-2">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-20 h-20 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center overflow-hidden">
                  {driver.images && driver.images.length > 0 ? (
                    <img 
                      src={`${BACKEND_URL}${driver.images[0].url}`} 
                      alt={driver.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-3xl font-bold text-[#D4AF37]">
                      {driver.name.charAt(0)}
                    </span>
                  )}
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {driver.name}
                  </h2>
                  <div className="flex items-center gap-2 mt-1">
                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                    <span className="font-medium">{driver.rating?.toFixed(1) || '5.0'}</span>
                    <span className="text-slate-400">•</span>
                    <Badge className={`capitalize ${driver.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}>
                      {driver.status}
                    </Badge>
                  </div>
                  <div className="flex gap-2 mt-3">
                    {driver.kyc_verified && (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        KYC
                      </Badge>
                    )}
                    {driver.police_verified && (
                      <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        PV
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <IndianRupee className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Monthly Salary</p>
                  <p className="font-semibold tabular-nums">{formatCurrency(driver.salary)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Operating Profit</p>
                  <p className={`font-semibold tabular-nums ${financials.operating_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatCurrency(financials.operating_profit)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Profit Per Driver Section */}
        <Card className="border-2 border-[#D4AF37]/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
              Profit Per Driver
              <Badge className="ml-2 bg-[#D4AF37]/10 text-[#D4AF37]">Operating Revenue - Operating Cost</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="p-4 bg-emerald-50 rounded-xl">
                <p className="text-sm text-slate-500">Operating Revenue</p>
                <p className="text-2xl font-bold text-emerald-600 tabular-nums">{formatCurrency(financials.total_revenue)}</p>
                <p className="text-xs text-slate-400 mt-1">Total revenue generated</p>
              </div>
              <div className="p-4 bg-red-50 rounded-xl">
                <p className="text-sm text-slate-500">Operating Cost</p>
                <p className="text-2xl font-bold text-red-600 tabular-nums">{formatCurrency(financials.total_operating_cost)}</p>
                <p className="text-xs text-slate-400 mt-1">Salary + Expenses</p>
              </div>
              <div className={`p-4 rounded-xl ${financials.operating_profit >= 0 ? 'bg-[#D4AF37]/10' : 'bg-red-100'}`}>
                <p className="text-sm text-slate-500">Operating Profit</p>
                <p className={`text-2xl font-bold tabular-nums ${financials.operating_profit >= 0 ? 'text-[#D4AF37]' : 'text-red-600'}`}>
                  {formatCurrency(financials.operating_profit)}
                </p>
                <p className="text-xs text-slate-400 mt-1">Revenue - Cost</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-xl">
                <p className="text-sm text-slate-500">Profit Margin</p>
                <p className="text-2xl font-bold text-blue-600 tabular-nums">{financials.profit_margin || 0}%</p>
                <p className="text-xs text-slate-400 mt-1">Efficiency ratio</p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-slate-50 rounded-xl">
              <h4 className="font-medium text-sm text-slate-500 mb-3">Cost Breakdown</h4>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-600">Annual Salary Cost</span>
                    <span className="font-medium tabular-nums">{formatCurrency(financials.annual_salary_cost)}</span>
                  </div>
                  <Progress value={financials.total_operating_cost > 0 ? (financials.annual_salary_cost / financials.total_operating_cost) * 100 : 0} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-600">Other Expenses</span>
                    <span className="font-medium tabular-nums">{formatCurrency(financials.total_expenses)}</span>
                  </div>
                  <Progress value={financials.total_operating_cost > 0 ? (financials.total_expenses / financials.total_operating_cost) * 100 : 0} className="h-2" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Images & Documents Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Driver Images */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                <ImageIcon className="w-5 h-5 text-[#D4AF37]" />
                Driver Photos
              </CardTitle>
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  ref={imageInputRef}
                  className="hidden"
                  data-testid="driver-image-input"
                />
                <Button 
                  size="sm" 
                  onClick={() => imageInputRef.current?.click()}
                  disabled={uploading}
                  className="bg-[#D4AF37] hover:bg-[#B5952F] text-black"
                  data-testid="upload-driver-image-btn"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                  Upload Photo
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {driver.images && driver.images.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {driver.images.map((img) => (
                    <div key={img.id} className="relative group">
                      <img
                        src={`${BACKEND_URL}${img.url}`}
                        alt={img.name}
                        className="w-full h-32 object-cover rounded-lg border"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                        <a href={`${BACKEND_URL}${img.url}`} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="secondary">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </a>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => handleDeleteImage(img.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500 mt-1 truncate">{img.name}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <ImageIcon className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>No photos uploaded</p>
                  <p className="text-sm">Upload driver photos for records</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Driver Documents */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                <FileText className="w-5 h-5 text-[#D4AF37]" />
                Driver Documents
              </CardTitle>
              <Dialog>
                <DialogTrigger asChild>
                  <Button 
                    size="sm"
                    className="bg-[#D4AF37] hover:bg-[#B5952F] text-black"
                    data-testid="upload-driver-doc-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Document
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Upload Document</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Document Type</Label>
                      <Select value={docType} onValueChange={setDocType}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {DOC_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>File</Label>
                      <Input
                        type="file"
                        onChange={handleDocumentUpload}
                        ref={docInputRef}
                        data-testid="driver-doc-input"
                      />
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {driver.documents && driver.documents.length > 0 ? (
                <div className="space-y-3">
                  {driver.documents.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-8 h-8 text-slate-400" />
                        <div>
                          <p className="font-medium text-sm">{doc.name}</p>
                          <Badge variant="outline" className="text-xs">{doc.doc_type}</Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <a href={`${BACKEND_URL}${doc.url}`} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="ghost">
                            <Download className="w-4 h-4" />
                          </Button>
                        </a>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDeleteDocument(doc.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>No documents uploaded</p>
                  <p className="text-sm">Upload License, Aadhar, etc.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Contact & License Info */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Playfair Display, serif' }}>
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">Phone</p>
                  <p className="font-medium">{driver.phone}</p>
                </div>
              </div>
              {driver.email && (
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Email</p>
                    <p className="font-medium">{driver.email}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">Date of Birth</p>
                  <p className="font-medium">{driver.date_of_birth}</p>
                </div>
              </div>
              <div className="pt-4 border-t">
                <p className="text-sm text-slate-500 mb-1">Address</p>
                <p className="font-medium">{driver.address}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Playfair Display, serif' }}>
                License & Employment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-slate-500">License Number</p>
                  {licenseStatus.status === 'Valid' ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <AlertTriangle className={`w-4 h-4 ${licenseStatus.color}`} />
                  )}
                </div>
                <p className="font-semibold">{driver.license_number}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-sm text-slate-500">Expires:</span>
                  <span className="text-sm font-medium">{driver.license_expiry}</span>
                  <span className={`text-sm ${licenseStatus.color}`}>({licenseStatus.status})</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-xl">
                  <p className="text-sm text-slate-500">Joining Date</p>
                  <p className="font-semibold">{driver.joining_date}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl">
                  <p className="text-sm text-slate-500">Violations</p>
                  <p className="font-semibold text-emerald-600">{driver.violations || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default DriverDetail;
